import { Button, NativeSelect } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { MessageResponse } from '@/api';
import { VerdictTag } from '@/components/Inbox/VerdictTag';
import { DataTable } from '@/components/Ui/DataTable';
import { Section } from '@/components/Ui/Section';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useVerdictPreference, type Verdict } from '@/lib/message-prefs';
import { firstLine, parseMessageData } from '@/lib/messages';
import { listTime } from '@/lib/relative-time';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

function messageFirstLine(m: MessageResponse): string {
  const data = parseMessageData(m);
  return data ? firstLine(data) : '';
}

export function RecentMessagesSection({ form, data }: Pick<SectionProps, 'form' | 'data'>) {
  const { t, i18n } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const total = data.summary?.messages_total;
  const messages = data.messages ?? [];
  const [verdict, setVerdict] = useVerdictPreference();
  const byForm = `form=${encodeURIComponent(form.id)}`;

  const rejectedParts = (data.rejections ?? [])
    .filter((r) => r.count > 0)
    .map((r) =>
      t(`form.recent.reason.${r.reason}`, {
        count: r.count,
        defaultValue: `${r.count} ${r.reason}`,
      })
    );
  const ours = (data.problems ?? []).filter((p) => p.group === 'ours').length;

  return (
    <Section
      title={t('form.recent.title')}
      actions={
        <>
          {total !== 0 && (
            <NativeSelect
              size="xs"
              aria-label={t('inbox.verdictLabel')}
              value={verdict}
              onChange={(e) => setVerdict(e.currentTarget.value as Verdict)}
              data={[
                { value: '', label: t('inbox.verdictAll') },
                { value: 'not_spam', label: t('inbox.verdictNotSpam') },
                { value: 'spam', label: t('inbox.verdictSpam') },
              ]}
            />
          )}
          {total !== 0 && (
            <Button
              component={Link}
              size="xs"
              to={createLanguageURL(`/account/messages?${byForm}&verdict=${verdict}`)}
            >
              {typeof total === 'number' && verdict === ''
                ? t('form.recent.all', { count: total })
                : t('form.recent.allPlain')}
            </Button>
          )}
        </>
      }
    >
      {data.status.messages === 'failed' ? (
        <div className={classes.empty}>{t('form.recent.loadFailed')}</div>
      ) : data.status.messages === 'ready' && messages.length === 0 ? (
        <div className={classes.empty}>
          {verdict && total !== 0 ? t('form.recent.noneFiltered') : t('form.recent.none')}
        </div>
      ) : (
        <DataTable<MessageResponse>
          caption={t('form.recent.title')}
          rows={messages}
          rowKey={(m) => m.id}
          empty={t('ui.loading')}
          columns={[
            {
              key: 'from',
              header: t('form.recent.from'),
              render: (m) => (
                <UiLink to={`/account/messages/${m.id}?${byForm}`}>
                  <strong>{m.sender_name || m.sender_email || t('form.recent.noSender')}</strong>
                </UiLink>
              ),
            },
            {
              key: 'line',
              header: t('form.recent.firstLine'),
              render: (m) => (
                <span className={classes.firstLine}>
                  {m.encryption_mode === 'nostr' ? t('form.recent.encrypted') : messageFirstLine(m)}
                </span>
              ),
            },
            {
              key: 'verdict',
              header: t('form.recent.verdict'),
              render: (m) => <VerdictTag message={m} filterOn={form.filter_spam} />,
            },
            {
              key: 'received',
              header: t('form.recent.received'),
              render: (m) => (
                <span className={classes.nowrap}>{listTime(m.created_at, i18n.language)}</span>
              ),
            },
          ]}
        />
      )}
      {(rejectedParts.length > 0 || ours > 0) && (
        <div className={classes.footer}>
          {rejectedParts.length > 0 && (
            <span className={classes.help}>
              {t('form.recent.rejected', { list: rejectedParts.join(', ') })}{' '}
              <UiLink to={`/account/forms/${form.id}/messages`}>{t('form.recent.details')}</UiLink>
            </span>
          )}
          {ours > 0 && (
            <span className={classes.help}>
              {' '}
              {t('form.recent.ours', { count: ours })}{' '}
              <UiLink to={`/account/problems?form=${encodeURIComponent(form.id)}`}>
                {t('form.recent.problems')}
              </UiLink>
            </span>
          )}
        </div>
      )}
    </Section>
  );
}
